import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as echarts from 'echarts';
import { useSelector } from 'react-redux';
import { BaseChart, Retry } from '@components/common';
import { DefaultLayer as Loading } from '@components/common/Retry/Elements';
import { useStackedDataHooks, useStackedGroupByHooks } from '../hooks/useStackedHooks';
import { buildStackedOption, getCodeColorMap, getStackedTimeSeries } from './utils';
import { realTimeModeOnTrendsSelector } from '@store/common/selectors';
import { useExternalState } from '../hooks/useExternalState';
import { NETWORK_REQUEST_RESPONSE_CODE_DIMENSION } from '../constants';
import { useAvailable } from '../hooks/useAvailable';
import NetworkContext from '../context';

const AVERAGE_REQUEST_COUNT_METRIC = 'AverageRequestCount';

function Stacked() {
  const { enableSSF, enableTimeSlice } = React.useContext(NetworkContext);
  const realTimeOnTrends = useSelector(realTimeModeOnTrendsSelector);
  const { interval } = useAvailable(enableSSF, enableTimeSlice);
  const { keepPreviousData, enableLoading } = useExternalState();
  const [instance, setInstance] = useState<echarts.ECharts | null>(null);
  const chartInstanceRefCb: any = useCallback((instance) => {
    setInstance(instance);
  }, []);

  const {
    isLoading: isGroupByLoading,
    filterList,
    refetch,
  }: any = useStackedGroupByHooks({
    interval,
    metric: AVERAGE_REQUEST_COUNT_METRIC,
  });

  const stackedData: any = useStackedDataHooks({
    filters: filterList,
    interval,
    metrics: [AVERAGE_REQUEST_COUNT_METRIC],
    enabled: !isGroupByLoading && !!filterList.length,
    keepPreviousData,
  });

  const { isLoading: isStackedTimeseriesLoading, data } = stackedData;

  const loading = isGroupByLoading || isStackedTimeseriesLoading;

  const chartData = useMemo(() => {
    if (!data) {
      return undefined;
    }
    const codes = filterList.map(({ value: code }) => code).reverse();
    const colorMap = getCodeColorMap(codes);
    return getStackedTimeSeries({
      data,
      series: codes,
      colorMap,
      groupBy: NETWORK_REQUEST_RESPONSE_CODE_DIMENSION,
      metric: AVERAGE_REQUEST_COUNT_METRIC,
      interval,
    });
  }, [interval, data, filterList]);

  useEffect(() => {
    if (instance !== null && !loading) {
      const option = buildStackedOption(chartData, realTimeOnTrends);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      instance.setOption(option, { notMerge: true });
    }
  }, [instance, chartData, realTimeOnTrends, loading]);

  return (
    <Retry fetchData={refetch} isLoading={loading} progress={true}>
      {enableLoading && loading && <Loading progress duration={1} />}
      <BaseChart
        key="network-code-chart"
        chartId="network-code-chart"
        chartInstanceRefCb={chartInstanceRefCb}
        style={{ height: '100%', minHeight: 310 }}
      />
    </Retry>
  );
}

export default Stacked;
